<script setup lang="ts">
import type { EmblaPluginType, EmblaOptionsType } from "embla-carousel";
import Autoplay from "embla-carousel-autoplay";
import type {PortfolioType, ReviewType} from "~/types/Portfolio";
import {RoleEnum} from "~/types/User";
import { Breadcrumb } from '~/components/ui/breadcrumb'
import TagBadge from '~/components/custom-ui/TagBadge.vue'
import PhotoViewer from '~/components/custom-ui/PhotoViewer.vue'
import { Dialog, DialogTrigger, DialogContent } from '~/components/ui/dialog'
import RatingStars from '~/components/custom-ui/RatingStars.vue'
import { Button } from '~/components/ui/button'
import { Carousel, CarouselContent, CarouselItem } from '~/components/ui/carousel'
import UserAvatar from '~/components/custom-ui/UserAvatar.vue'
import SuggestOrder from '~/components/pages/catalog/SuggestOrder.vue'
import AuthMainElement from '~/components/layouts/AuthMainElement.vue'

const { t, locale } = useI18n();
const Loader = useIsLoadingStore()

const userStore = useUserStore();

const props = defineProps({
    portfolio: {
        type: Object as PropType<PortfolioType>,
        required: true,
    },
    educationTypeList: {
        type: Array,
        default: () => []
    },
    specification_name: {
        type: String,
        default: null
    },
    review_tags: {
        type: Array,
        default: () => []
    },
    reviews: {
        type: Array as PropType<ReviewType[]>,
        default: () => []
    },
    reviews_total: {
        type: Number,
        default: 0
    },
    similarSpecialists: {
        type: Array as PropType<PortfolioType[]>,
        default: () => []
    },
})
const emit = defineEmits(['loadMoreReviews', 'changeMode'])

const masterSpecificationIds = computed(() => {
    return props.portfolio.specifications.map(item => item.documentId)
})

const breadcrumbs = [
    {
        label: t('Home'),
        url: '/',
    },
    {
        label: t('Catalog'),
        url: '/catalog',
    },
];

function extractYouTubeVideoID(url: string): string | null {
    const regex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
    const match = url.match(regex);
    return match ? match[1] : null;
}

const carouselContainerRef = ref<InstanceType<typeof Carousel> | null>(null) // TODO: Поправить слайдер в арабской версии https://stackblitz.com/edit/github-gmh6db-bjqkxt?file=app.vue

const key = ref(1)
const direction = computed(() => locale.value === 'ar' ? 'rtl' : 'ltr')

const plugins = ref<EmblaPluginType[]>([])
const autoplay = Autoplay({
    delay: 2000,
    stopOnMouseEnter: true,
    stopOnInteraction: false,
})
plugins.value.push(autoplay)

const carousel_options = ref<EmblaOptionsType>({
    align: 'start',
    //loop: false,
    skipSnaps: true,
    direction: direction.value
})

/* watch(locale, async (newLocale) => {
    carousel_options.value.direction = direction.value
    await nextTick(() => {
        carouselContainerRef.value?.carouselApi?.reInit(carousel_options.value)
        autoplay.reset()
    })
}); */
</script>

<template>
    <div class="grid grid-cols-7 xl:grid-cols-10 gap-10 md:gap-12 mt-10 mb-10 md:mb-40">
        <div class="order-2 md:order-1 md:col-span-7 xl:col-span-7 col-span-full">
            <div class="mb-14">
                <Breadcrumb :items="breadcrumbs" use_last_seperator item_class="text-lg" separator_size="18px"
                                    class="mb-4 md:block hidden" />
                <p class="description-title mb-4 md:mb-5">
                    {{ $t("About specialist") }}
                </p>

                <div v-if="portfolio.specifications.length"
                     class="flex flex-wrap gap-2  mt-2 w-fit">
                    <TagBadge v-for="tag in portfolio.specifications" variant="specification">{{ tag.name }}
                    </TagBadge>
                </div>

                <p v-if="portfolio.description" class="mt-2 font-medium text-sm">
                    {{ portfolio.description }}
                </p>


                <div v-if="portfolio.work_experience" class="mt-5 md:mt-3">
                    <p class="mb-2 text-sm font-medium">{{ $t("Work experience") }}</p>
                    <TagBadge>{{ t('Over {years} years', { years: portfolio.work_experience }) }}
                    </TagBadge>
                </div>

                <template v-if="portfolio.images?.length || portfolio.video_links?.length">
                    <div class="master__protfolio mt-4">
                        <p class="mb-2 text-sm font-medium">
                            {{ $t("Photos & video") }}
                        </p>
                        <div>
                            <Carousel
                                    v-if="portfolio.images?.length || portfolio.video_links?.length"
                                    class="relative w-full"
                                    :opts="{
                                        align: 'start',
                                    }"
                            >
                                <CarouselContent  class="-ml-4">
                                    <CarouselItem
                                            v-for="(img, index) in portfolio.images"
                                            class="pl-4 basis-[141px] h-[125px]"
                                    >
                                        <PhotoViewer :url="img.formats?.large?.url || img.formats?.medium?.url" :thumbnail="img.formats?.thumbnail?.url" class="aspect-square relative overflow-hidden rounded-xl" />
                                    </CarouselItem>

                                    <CarouselItem
                                            v-for="(video, index) in portfolio.video_links"
                                            class="pl-4 basis-[238px] h-[125px]"
                                    >
                                        <Dialog>
                                            <DialogTrigger>
                                                <div class="aspect-video relative overflow-hidden rounded-xl">
                                                    <img
                                                            :src="`https://img.youtube.com/vi/${extractYouTubeVideoID(video.url)}/maxresdefault.jpg`"
                                                            class="object-cover w-full h-full"
                                                            :alt="'Video thumbnail ' + (index + 1)"
                                                    />
                                                    <div class="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-white/70 transition-colors">
                                                        <Icon
                                                                name="ri:youtube-fill"
                                                                class="w-10 h-10 text-white"
                                                        />
                                                    </div>
                                                </div>
                                            </DialogTrigger>

                                            <DialogContent class="sm:max-w-[90vw] sm:max-h-[80vh]">
                                                <div class="aspect-video w-full">
                                                    <iframe
                                                            class="w-full h-full"
                                                            :src="`https://www.youtube.com/embed/${extractYouTubeVideoID(video.url)}?autoplay=1`"
                                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                            allowfullscreen
                                                    />
                                                </div>
                                            </DialogContent>
                                        </Dialog>
                                    </CarouselItem>
                                </CarouselContent>
                            </Carousel>
                        </div>
                    </div>
                </template>
            </div>


            <div v-if="reviews.meta?.total" class="mt-10">
                <p class="description-title">
                    {{ $t("Reviews") }}
                </p>

                <template v-if="review_tags.length || reviews.length">
                    <div v-if="review_tags.length" class="mt-4">
                        <p class="mb-2 text-sm font-medium">{{ $t("Feedback from client") }}</p>
                        <div class="flex flex-wrap gap-2 w-fit">
                            <TagBadge v-for="tag in review_tags">{{ tag?.name }}</TagBadge>
                        </div>
                    </div>

                    <div v-if="reviews?.meta?.total" class="mt-4">
                        <p class="mb-2 text-sm font-medium">{{ $t("Recommended Reviews") }}</p>
                        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            <div v-for="review in reviews.data"
                                 class="border border-gray-200 p-4 flex flex-col justify-between rounded-lg">
                                <div class="top_row">
                                    <p v-if="review.owner?.username" class="text-sm">{{ review.owner.username }}</p>
<!--                                    <p class="text-xs text-gray-500">{{ review.customer_specification }}</p>-->
                                    <p v-if="review.text" class="mt-2 text-sm break-words">{{ review.text }}</p>
                                </div>
                                <div class="bottom_row flex items-end justify-between mt-2">
                                    <RatingStars :rating="review.rating" />
                                    <p class="text-[10px]">{{ $formatDateTime(review.createdAt, 'MMMM dd, yyyy') }}</p>
                                </div>
                            </div>
                        </div>
                        <Button v-if="reviews?.meta?.total > reviews.length" :disabled="Loader.isLoading" @click.prevent="emit('loadMoreReviews')"
                                  variant="outline"
                                  class="text-sm px-7 py-2.5 mt-2 rounded-lg border-primary-500 !text-primary-500 hover:bg-primary-50 hover:border-primary-50  transition-all">
                            <Icon v-if="Loader.isLoading" name="svg-spinners:ring-resize" />
                            <span v-else>{{ $t("Load more") }}</span>
                        </Button>
                    </div>
                </template>

                <div v-else class="mt-4">
                    <p>{{ $t('No reviews yet') }}</p>
                </div>
            </div>

            <div v-if="similarSpecialists.length" class="mt-10">
                <p class="description-title mb-7">
                    {{ $t("Similar specialists") }}
                </p>

                <Carousel :key="key" ref="carouselContainerRef" :opts="carousel_options" :plugins="plugins"
                            class="relative">
                    <CarouselContent class="ltr:-ml-7 rtl:-mr-7">
                        <CarouselItem v-for="item in similarSpecialists" :key="item.documentId"
                                        class="bazis-[100%] xl:basis-[30%] ltr:pl-7 rtl:pr-7">
                            <NuxtLink :to="{name: 'portfolio', params: {id: item.documentId}}">
                                <div class="bg-stroke rounded-[36px] p-6">
                                    <div class="flex items-center">
                                        <UserAvatar :src="item.user.avatar?.formats?.small?.url" :name="item.user.username" size="sm" />
                                        <div class="ltr:ml-3 rtl:mr-3">
                                            <p class="mb-2 font-medium">{{ item.user.username }}</p>

                                            <div class="text-xs text-gray-500">
                                                <div v-if="item.user.compute_rating" class="flex items-center">
                                                    <span>{{ $t('User rating') }} :</span>
                                                    <div class="flex items-center">
                                                        <Icon name="ri:star-fill" size="16px"
                                                              class="text-[#F2AE40] mx-1" />
                                                        <span>{{ item.user.compute_rating?.toFixed(1) }}</span>
                                                    </div>
                                                </div>
                                                <p v-if="item?.completed_tasks_count">{{ `${$t('Completed')} ${item?.completed_tasks_count} ${$t('tasks')}`
                                                    }}</p>
                                            </div>
                                        </div>
                                    </div>
                                    <p v-if="item?.description"
                                       class="text-xs text-gray-500 line-clamp-4 mt-4">{{
                                            item?.description }}</p>
                                </div>
                            </NuxtLink>
                        </CarouselItem>
                    </CarouselContent>
                    <div
                        class="overflow_fade absolute ltr:right-0 rtl:left-0 top-0 h-full ltr:bg-gradient-to-l rtl:bg-gradient-to-r from-white to-transparent w-[100px]">
                    </div>
                </Carousel>
            </div>
        </div>

        <div class="order-1 md:order-2 col-span-full md:col-span-4  xl:col-span-3 md:p-6 rounded-2xl md:border md:border-gray-200 h-fit">
            <div class="grid gap-4 grid-flow-col cstm_style_avatar w-fit md:w-auto">
                <UserAvatar :src="portfolio.user.avatar?.formats?.small?.url" :name="portfolio.user.username" size="xl" class="lg:w-full !items-start"
                                    shape="square" />

                <div class="flex flex-col justify-between h-full">
                    <div class="top_row">
                        <p class="text-lg font-medium">{{ portfolio.user.username }}</p>
                        <p class="text-gray-500 text-sm">{{ specification_name }}</p>
                    </div>
                    <div class="bottom_row">
                        <div v-if="portfolio.user.compute_rating" class="flex items-center">
                            <RatingStars :rating="portfolio.user.compute_rating" />
                            <span class="text-sm ltr:ml-1 rtl:mr-1 leading-none">({{ portfolio?.user?.compute_rating?.toFixed(1) || 0
                                }})</span>
                        </div>

                        <div v-if="portfolio.user.compute_review_count" class="flex items-center justify-start mt-1">
                            <Icon class="text-gray-500" size="24" name="ri:chat-heart-line" />
                            <p class="text-sm ltr:ml-1 rtl:mr-1 leading-none"> {{ portfolio.user.compute_review_count }}
                                {{ $t('reviews') }}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div class="mt-4" v-if="portfolio.education">
                <p class="mb-1 text-sm font-medium">{{ $t("Education") }}</p>
                <p class="mr-1 catalog-portfolio-user-tag w-fit"> {{ portfolio.education.name }}</p>
            </div>

            <div class="mt-4" v-if="portfolio.user.languages?.length">
                <p class="mb-1 text-sm font-medium">{{ $t("Speaking in") }}</p>
                <div class="flex flex-wrap gap-1">
                    <p v-for="lang in portfolio.user.languages" class="catalog-portfolio-user-tag w-fit"> {{ lang.name }}</p>
                </div>
            </div>
            
            <div v-if="portfolio?.city" class="mt-4">
                <p class="mb-1 text-sm font-medium">{{ $t("Location") }}</p>
                <div>
                    <p class="mr-1 mb-1 catalog-portfolio-user-tag w-fit">{{ portfolio.city?.name }}</p>
                </div>
            </div>
            
            <div v-if="portfolio?.geo?.lat && portfolio?.geo?.lng" class="mt-4">
                <div @click.prevent="navigateTo({name: 'portfolio', params: {id: portfolio.documentId}, query: {mode: 'geo'}})" class="rounded-md border border-gray-200 overflow-hidden bg-cover p-2 flex items-center cursor-pointer hover:py-3 transition-all"
                     style="background-image: url('/img/map_holder.png')">
                    <Icon name="ri:map-pin-2-line" size="24px" class="text-gray-600 ltr:mr-1.5 rtl:ml-1.5" />
                    <span class="text-sm font-medium">{{ $t('Map location') }}</span>
                </div>
            </div>
            

            <template v-if="userStore.user?.user_role != RoleEnum.MASTER">
                <SuggestOrder
                    v-if="userStore.isAuthenticated"
                    :master_id="portfolio.documentId"
                    :specification_id="portfolio.specifications[0].parent_id"
                    :onlySpecificationIds="masterSpecificationIds"
                    btn_class="w-full mt-4"
                />
                <AuthMainElement
                    v-else
                    :icon="false"
                    :title="$t('Suggest order')"
                    className="mt-4 w-full rounded-lg bg-primary-500 hover:bg-primary-600" />
            </template>
        </div>
    </div>
</template>

<style scoped lang="scss">
.catalog-portfolio-user-tag {
    height: 24px;
    padding: 4px 8px 4px 8px;
    gap: 3px;
    border-radius: 6px;
    border: 1px solid rgba(226, 228, 233, 1);
    background: rgba(255, 255, 255, 1);
    color: rgba(10, 13, 20, 1);
    font-size: 12px;
    font-weight: 500;
    line-height: 16px;
    text-align: center;
}

.description-title {
    font-family: Involve, sans-serif;
    font-size: 26px;
    font-weight: 500;
    line-height: 34.58px;
    text-align: start;
    @media(max-width: 768px){
        font-size: 18px;
        line-height: 24px;
    }
}
</style>